<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const receivingTypes = [
  {
    icon: 'ri-file-list-3-line',
    color: 'warning',
    title: 'Receiving dari Purchase Order',
    desc: 'Barang datang sesuai PO yang sudah dibuat sebelumnya. Sistem akan otomatis pre-fill item dari PO — Anda tinggal cek dan sesuaikan jumlah yang benar-benar datang.',
    tag: 'Direkomendasikan',
    tagColor: 'success',
  },
  {
    icon: 'ri-add-box-line',
    color: 'info',
    title: 'Direct Receiving (Tanpa PO)',
    desc: 'Barang datang tiba-tiba tanpa PO sebelumnya. Misalnya supplier datang langsung ke toko atau Anda beli di pasar secara spontan. Stok tetap masuk, tapi tidak ada catatan PO-nya.',
    tag: 'Untuk pembelian dadakan',
    tagColor: 'info',
  },
]

const itemFields = [
  { field: 'Bahan Baku', required: true, desc: 'Pilih bahan baku yang diterima. Jika dari PO, sudah terisi otomatis.', example: '"Kopi Arabika Gayo"' },
  { field: 'Satuan Pembelian', required: true, desc: 'Satuan sesuai nota/faktur supplier — boleh satuan dasar bahan atau satuan turunannya (mis. Kilogram) kalau sudah dihubungkan di menu Satuan. Jika dari PO, sudah terisi otomatis mengikuti satuan yang dipakai di PO.', example: '"Kilogram" (untuk bahan bersatuan dasar Gram)' },
  { field: 'Qty Diterima', required: true, desc: 'Jumlah yang benar-benar datang, dalam Satuan Pembelian di atas. Boleh berbeda dari jumlah di PO.', example: '"0.8" (padahal PO 1 Kg — kekurangan 200 gram)' },
  { field: 'Total Harga Pembelian', required: true, desc: 'Total uang yang dibayar untuk baris item ini (Rupiah), sesuai faktur aktual dari supplier — bukan harga per satuan.', example: '"18000" (Rp18.000 untuk 1 Kg)' },
  { field: 'HPP per Satuan Dasar', required: false, desc: 'Read-only, informasi saja — dihitung otomatis realtime dari Total Harga ÷ Qty, dikonversi ke satuan dasar bahan. Angka inilah yang dipakai sistem untuk update average cost saat dikonfirmasi.', example: '"Rp 18/gram" (otomatis)' },
]

const faq = [
  {
    q: 'Apa yang terjadi dengan stok setelah Receiving dikonfirmasi?',
    a: 'Stok bahan baku langsung bertambah sesuai jumlah yang diterima. Sistem juga otomatis menghitung ulang average cost (harga rata-rata) bahan tersebut berdasarkan harga di receiving. Proses ini tidak bisa dibatalkan, jadi pastikan data sudah benar sebelum konfirmasi.',
  },
  {
    q: 'Bagaimana jika jumlah barang yang datang kurang dari yang dipesan?',
    a: 'Isi saja qty yang benar-benar datang. Contoh: PO 5 kg kopi, yang datang hanya 4.8 kg — isi "4800" gram di receiving. Status PO akan berubah jadi "Sebagian Diterima". Anda bisa buat receiving baru nanti saat sisa 200 gram datang.',
  },
  {
    q: 'Apakah bisa buat Receiving tanpa Purchase Order?',
    a: 'Bisa. Pilih opsi "— Direct Receiving (tanpa PO) —" di dropdown Purchase Order, lalu isi item secara manual. Ini cocok untuk pembelian mendadak di pasar atau saat supplier datang tanpa pemberitahuan sebelumnya.',
  },
  {
    q: 'Saya salah input qty saat konfirmasi Receiving. Bisa dibatalkan?',
    a: 'Tidak bisa. Setelah receiving dikonfirmasi, stok sudah masuk dan tidak bisa di-undo. Solusinya: lakukan Stock Adjustment (penyesuaian stok) untuk koreksi stok yang salah. Hubungi panduan Stock Adjustment untuk detailnya.',
  },
  {
    q: 'Receiving saya masih Draft sudah beberapa hari. Stok belum berubah, ya?',
    a: 'Benar. Selama masih Draft, stok belum berubah sama sekali. Stok hanya bertambah setelah Anda klik "Konfirmasi & Masukkan Stok". Draft bisa Anda simpan dulu sambil menunggu barang diverifikasi fisik.',
  },
  {
    q: 'Apakah harga di Receiving harus sama dengan harga di PO?',
    a: 'Tidak harus. Anda bebas mengisi Total Harga Pembelian aktual yang tertera di faktur dari supplier, meskipun berbeda dari estimasi di PO. Harga inilah yang dipakai sistem untuk menghitung HPP per satuan dasar dan kalkulasi average cost bahan baku.',
  },
  {
    q: 'Saya terima barang dalam Kilogram tapi stok bahannya dalam Gram — apakah harus dikonversi manual dulu?',
    a: 'Tidak. Pilih "Kilogram" di kolom Satuan Pembelian (asalkan sudah dihubungkan ke Gram di menu Satuan), isi Qty Diterima dalam Kilogram dan Total Harga Pembelian totalnya. Sistem otomatis mengonversi ke gram dan menghitung HPP per gram saat receiving dikonfirmasi — Anda tidak perlu menghitung apa pun secara manual.',
  },
]

const steps = [
  {
    no: 1,
    icon: 'ri-truck-arrive-line',
    color: 'warning',
    title: 'Barang Tiba dari Supplier',
    desc: 'Supplier mengirimkan barang beserta faktur atau surat jalan.',
  },
  {
    no: 2,
    icon: 'ri-search-eye-line',
    color: 'info',
    title: 'Cek Fisik Barang',
    desc: 'Hitung dan periksa kondisi barang yang datang. Catat jika ada kekurangan atau kerusakan.',
  },
  {
    no: 3,
    icon: 'ri-add-box-line',
    color: 'primary',
    title: 'Buat Receiving di Sistem',
    desc: 'Input jumlah yang benar-benar diterima. Jika ada PO, sistem sudah pre-fill datanya.',
  },
  {
    no: 4,
    icon: 'ri-check-double-line',
    color: 'success',
    title: 'Konfirmasi → Stok Masuk',
    desc: 'Klik Konfirmasi. Stok otomatis bertambah dan average cost diperbarui.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <div class="d-flex align-center gap-2 mb-3">
      <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
        User Guide
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <RouterLink to="/userguide/pembelian" class="text-decoration-none text-medium-emphasis text-body-2">
        Pembelian
      </RouterLink>
      <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
      <span class="text-body-2 font-weight-medium">Receiving Barang</span>
    </div>

    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="success" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-archive-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">Receiving Barang</h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Catat penerimaan barang dari supplier — stok masuk otomatis setelah dikonfirmasi
            </p>
          </div>
        </div>
        <VAlert type="info" variant="tonal">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda mencatat barang yang datang dari supplier dan mengkonfirmasikannya
          agar stok bahan baku di sistem bertambah secara otomatis.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="success" />
            Apa itu Receiving dan Mengapa Penting?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Receiving</strong> adalah proses pencatatan penerimaan barang yang datang dari supplier.
              Ibaratnya seperti saat Anda beli di minimarket — kasir men-scan barang masuk,
              dan stok di sistem bertambah. Di PintuKasir, Anda yang mencatat sendiri
              barang apa saja yang baru datang dan berapa jumlahnya.
            </p>

            <VAlert type="success" variant="tonal" class="mb-3">
              <strong>Contoh nyata:</strong>
              Senin pagi, kurir dari CV Agro datang bawa 5 kg kopi Arabika dan 10 liter susu.
              Anda buka PintuKasir → buat Receiving → isi qty sesuai yang datang →
              klik Konfirmasi. <strong>Selesai. Stok kopi dan susu langsung bertambah otomatis.</strong>
              Tidak perlu update stok manual lagi.
            </VAlert>

            <p class="text-body-2 mb-0">
              Receiving juga mencatat <strong>harga beli aktual</strong> dari faktur supplier.
              Sistem akan gunakan data ini untuk menghitung
              <strong>average cost</strong> (harga pokok rata-rata) bahan baku Anda,
              yang nantinya mempengaruhi perhitungan HPP produk.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Alur Proses -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-flow-chart" class="me-2" color="primary" />
            Alur Proses Receiving
          </VCardTitle>
          <VCardText>
            <VRow>
              <VCol
                v-for="(step, i) in steps"
                :key="step.no"
                cols="12"
                sm="6"
                md="3"
              >
                <div class="d-flex flex-column align-center text-center pa-2">
                  <VAvatar :color="step.color" variant="tonal" size="52" class="mb-3 position-relative">
                    <VIcon :icon="step.icon" size="26" />
                    <VChip
                      size="x-small"
                      color="grey"
                      variant="elevated"
                      class="position-absolute"
                      style="top: -4px; right: -4px; min-width: 20px;"
                    >
                      {{ step.no }}
                    </VChip>
                  </VAvatar>
                  <p class="text-body-2 font-weight-bold mb-1">{{ step.title }}</p>
                  <p class="text-caption text-medium-emphasis mb-0">{{ step.desc }}</p>
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Jenis Receiving -->
    <VRow class="mb-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">📦 Dua Jenis Receiving</h5>
        <VRow>
          <VCol v-for="rt in receivingTypes" :key="rt.title" cols="12" md="6">
            <VCard variant="outlined" class="h-100">
              <VCardText class="pa-4">
                <div class="d-flex align-center gap-3 mb-3">
                  <VAvatar :color="rt.color" variant="tonal" size="44">
                    <VIcon :icon="rt.icon" size="24" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ rt.title }}</p>
                    <VChip :color="rt.tagColor" size="x-small" variant="tonal">
                      {{ rt.tag }}
                    </VChip>
                  </div>
                </div>
                <p class="text-body-2 text-medium-emphasis mb-0">{{ rt.desc }}</p>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VCol>
    </VRow>

    <!-- Cara Buat Receiving -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-add-circle-line" class="me-2" color="success" />
            Cara Membuat Receiving Baru
          </VCardTitle>
          <VCardText>
            <VAlert type="success" variant="tonal" class="mb-4 text-body-2">
              <strong>Cara tercepat:</strong>
              Buka halaman <strong>detail Purchase Order</strong> yang sudah berstatus "Dikirim" →
              klik tombol <strong>"Buat Receiving"</strong> (hijau).
              Sistem akan otomatis membawa Anda ke form receiving dengan data dari PO sudah terisi.
            </VAlert>

            <p class="text-body-2 font-weight-semibold mb-2">Atau buat manual dari menu Receiving:</p>

            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-3">
                Buka menu <strong>Pembelian → Receiving Barang</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                Klik tombol <strong>"+ Buat Receiving"</strong> di pojok kanan atas
              </li>
              <li class="mb-3">
                <p class="mb-2">Isi <strong>informasi dasar:</strong></p>
                <ul class="ps-3">
                  <li class="mb-2">
                    <strong>Purchase Order (opsional):</strong>
                    Pilih PO yang barangnya datang. Sistem akan otomatis pre-fill item dari PO tersebut.
                    Pilih "Direct Receiving" jika tidak ada PO.
                  </li>
                  <li class="mb-2">
                    <strong>Tanggal Terima (wajib):</strong>
                    Tanggal barang benar-benar datang. Default: hari ini.
                    Contoh: "14 Juli 2026"
                  </li>
                  <li class="mb-0">
                    <strong>Catatan (opsional):</strong>
                    Misalnya "ada 2 botol susu yang retak, dikembalikan ke supplier".
                  </li>
                </ul>
              </li>
              <li class="mb-3">
                <p class="mb-2">
                  Periksa dan sesuaikan <strong>tabel item</strong>:
                </p>
                <VTable>
                  <thead>
                    <tr>
                      <th style="min-width: 130px">Kolom</th>
                      <th style="min-width: 70px">Wajib?</th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in itemFields" :key="f.field">
                      <td><strong>{{ f.field }}</strong></td>
                      <td>
                        <VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        {{ f.desc }}
                        <br>
                        <span class="text-caption text-medium-emphasis">Contoh: {{ f.example }}</span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
                <VAlert type="warning" variant="tonal" class="mt-3 text-body-2">
                  <strong>Perhatikan Qty Diterima!</strong>
                  Jangan asal salin dari PO. Isi jumlah yang <em>benar-benar ada di depan Anda</em>
                  saat itu. Jika ada kurang atau lebih, isi sesuai kenyataan.
                </VAlert>
              </li>
              <li class="mb-3">
                Klik <strong>"Simpan (Draft)"</strong>. Receiving tersimpan tapi stok belum masuk.
              </li>
              <li>
                Setelah yakin data sudah benar, lanjut ke langkah konfirmasi di bawah.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Konfirmasi -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-check-double-line" class="me-2" color="success" />
            Konfirmasi Receiving — Stok Masuk Otomatis
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Ini adalah langkah terpenting. Setelah konfirmasi, stok bahan baku langsung bertambah.
            </p>
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-2">Buka halaman <strong>detail Receiving</strong></li>
              <li class="mb-2">Periksa ulang semua item dan qty</li>
              <li class="mb-2">Klik tombol <strong>"Konfirmasi & Masukkan Stok"</strong> (hijau)</li>
              <li class="mb-2">Baca peringatan di dialog konfirmasi</li>
              <li>Klik <strong>"Ya, Konfirmasi & Masukkan Stok"</strong> ✅</li>
            </ol>

            <VRow>
              <VCol cols="12" md="6">
                <VCard color="success" variant="tonal">
                  <VCardText class="pa-3">
                    <p class="text-body-2 font-weight-bold mb-2">
                      ✅ Yang Terjadi Setelah Konfirmasi:
                    </p>
                    <ul class="text-body-2 ps-4 mb-0">
                      <li class="mb-1">Qty diterima otomatis dikonversi ke satuan dasar bahan (mis. 1 Kg → 1.000 gram) sebelum ditambahkan ke stok</li>
                      <li class="mb-1">Stok bahan baku bertambah sesuai hasil konversi tersebut</li>
                      <li class="mb-1">Average cost bahan diperbarui berdasarkan HPP per satuan dasar hasil hitung otomatis (bukan harga per satuan pembelian)</li>
                      <li class="mb-1">Status Receiving berubah menjadi "Dikonfirmasi"</li>
                      <li class="mb-0">Status PO berubah (jika semua item terpenuhi → "Selesai")</li>
                    </ul>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="6">
                <VCard color="error" variant="tonal">
                  <VCardText class="pa-3">
                    <p class="text-body-2 font-weight-bold mb-2">
                      ⚠️ Yang Perlu Diperhatikan:
                    </p>
                    <ul class="text-body-2 ps-4 mb-0">
                      <li class="mb-1">Konfirmasi <strong>tidak bisa dibatalkan</strong></li>
                      <li class="mb-1">Stok yang sudah masuk <strong>tidak bisa di-undo</strong></li>
                      <li class="mb-1">Jika ada kesalahan qty, gunakan Stock Adjustment</li>
                      <li class="mb-0">Pastikan cek fisik barang sebelum konfirmasi</li>
                    </ul>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard color="success" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Jangan konfirmasi sebelum cek fisik barang.</strong>
                  Biasakan buka aplikasi <em>di samping barang yang baru datang</em>.
                  Hitung dulu, baru input. Ini mencegah selisih stok yang menyebabkan
                  kehabisan bahan padahal stok sistem masih ada.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Simpan sebagai Draft dulu jika barang masih diurutkan.</strong>
                  Tidak perlu langsung konfirmasi. Simpan Draft, sortir barang ke gudang,
                  lalu konfirmasi setelah yakin hitungannya benar.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Gunakan harga faktur, bukan harga estimasi PO.</strong>
                  Harga di receiving menentukan average cost. Selalu isi sesuai
                  harga yang tertera di faktur/nota dari supplier — bisa saja berbeda
                  dari yang diestimasikan di PO karena naik harga atau diskon.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Catat kekurangan di kolom Catatan.</strong>
                  Jika barang kurang atau ada yang rusak, tulis di kolom Catatan receiving.
                  Misalnya: "Kopi kurang 200 gram, supplier janji kirim hari Rabu".
                  Ini jadi bukti tertulis jika ada klaim ke supplier nanti.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mb-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Next Steps -->
    <VRow>
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Panduan Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Purchase Order', sub: 'Buat PO sebelum barang datang', icon: 'ri-file-list-3-line', color: 'warning', to: '/userguide/pembelian/purchase-order' },
              { title: 'Stock Adjustment', sub: 'Koreksi stok jika ada kesalahan receiving', icon: 'ri-equalizer-line', color: 'error', to: '/userguide/produk-inventory/stock-adjustment' },
              { title: 'Pergerakan Stok', sub: 'Lihat riwayat stok masuk dari receiving', icon: 'ri-exchange-line', color: 'secondary', to: '/userguide/produk-inventory/pergerakan-stok' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
